import React from "react";
import { Alert, Card, Row, Col, Typography, Space, Tag, Divider } from "antd";
import FlightInfoForm from "../../../../../components/common/FlightInfoForm";
import dayjs from "dayjs";
import FlightTimeDisplay from "../../../../../components/DateTimeDisplay";

const { Title, Text } = Typography;

/**
 * MVT航班更新处理组件
 * V5.0 更新：标准化处理器接口
 * 处理MVT报文引起的航班信息更新建议
 */
const MvtFlightUpdateHandler = ({
    actionItem,
    onConfirm,
    onReject,
    loading = false,
    readOnly = false,
    // 向后兼容的额外参数
    currentFlightData,
    onParametersChange,
    airportsMap = {},
}) => {
    // V3.0 兼容性修改：优先从 system_operations[0].parameters.updates 获取参数
    // 同时兼容旧的 action_parameters 结构和已执行的 execution_results
    const getActionParameters = () => {
        const suggestedPayload = actionItem.suggested_payload;
        const additionalInfo = actionItem.additional_info;

        if (suggestedPayload?.system_operations?.length > 0) {
            const firstOperation = suggestedPayload.system_operations[0];
            if (firstOperation.parameters?.updates) {
                return firstOperation.parameters.updates;
            }
        }

        if (additionalInfo?.execution_results?.length > 0) {
            const firstResult = additionalInfo.execution_results[0];
            if (firstResult?.command?.payload) {
                return firstResult.command.payload;
            }
        }

        return suggestedPayload?.action_parameters || {};
    };

    const actualDataParams = getActionParameters();
    const contextFlightDetails = actionItem.suggested_payload?.context_data_summary?.flight_details || {};
    const rawContent = actionItem.raw_input_content || "";

    const prepareCurrentValues = () => {
        // currentValues 严格反映 currentFlightData (系统当前航班状态)
        return {
            flight_number: currentFlightData?.flight_number,
            departure_airport: currentFlightData?.departure_airport,
            arrival_airport: currentFlightData?.arrival_airport,
            std: currentFlightData?.std,
            sta: currentFlightData?.sta,
            etd: currentFlightData?.etd,
            eta: currentFlightData?.eta,
            atd: currentFlightData?.atd,
            ata: currentFlightData?.ata,
            atd_icao: currentFlightData?.atd_icao,
            ata_icao: currentFlightData?.ata_icao,
            aircraft_reg: currentFlightData?.aircraft_reg,
            delay_code: currentFlightData?.delay_code,
            delay_minutes: currentFlightData?.delay_minutes,
            remarks: currentFlightData?.remarks,
        };
    };

    const prepareSuggestedValues = () => {
        // suggestedValues 仅包含 MVT 报文明确建议修改的字段及其建议值
        const suggested = {};

        if (actualDataParams.hasOwnProperty("new_atd_utc")) suggested.atd = actualDataParams.new_atd_utc;
        if (actualDataParams.hasOwnProperty("new_ata_utc")) suggested.ata = actualDataParams.new_ata_utc;
        if (actualDataParams.hasOwnProperty("new_etd_utc")) suggested.etd = actualDataParams.new_etd_utc;
        if (actualDataParams.hasOwnProperty("new_eta_utc")) suggested.eta = actualDataParams.new_eta_utc;
        if (actualDataParams.hasOwnProperty("new_atd_icao_utc")) suggested.atd_icao = actualDataParams.new_atd_icao_utc;
        if (actualDataParams.hasOwnProperty("new_ata_icao_utc")) suggested.ata_icao = actualDataParams.new_ata_icao_utc;

        if (actualDataParams.hasOwnProperty("delay_code")) suggested.delay_code = actualDataParams.delay_code;
        if (actualDataParams.mvt_full_data?.hasOwnProperty("delay_duration")) {
            suggested.delay_minutes = actualDataParams.mvt_full_data.delay_duration;
        }
        if (actualDataParams.hasOwnProperty("aircraft_reg")) suggested.aircraft_reg = actualDataParams.aircraft_reg;

        // MVT通常不建议修改这些核心计划信息，但如果AI建议中包含，也应采纳
        // 这些字段通常用于展示差异，如果MVT没有建议，则 suggested 中不应包含它们，
        // 这样 FlightInfoForm 才能正确地将它们与 currentValues 中的同名字段进行比较
        // 以高亮显示实际的"建议变更"。
        // FlightInfoForm 会将 currentValues 中有而 suggestedValues 中没有的字段视为未建议修改。

        // 以下字段通常由MVT报文的上下文提供，而不是直接的"建议修改项"
        // 但为了让FlightInfoForm能完整展示航班信息（即使MVT只更新了ATD），
        // 我们需要从currentValues (即系统当前数据) 或 contextFlightDetails (AI理解的上下文) 中填充它们。
        // 但 FlightInfoForm 的 initialValues (即 suggestedValues) 主要用于展示 *建议的* 更改。

        // 所以，对于航班号、航段、STD、STA等通常不由MVT直接修改的字段，
        // 它们应该由 FlightInfoForm 从 currentValues 中获取并显示。
        // suggestedValues 中只应包含 MVT 真正 *建议要变成* 的值。

        return suggested;
    };

    const handleValuesChange = (changedValues, allValues) => {
        const updatedParams = { ...actualDataParams };
        if ("atd" in changedValues) updatedParams.new_atd_utc = changedValues.atd;
        if ("ata" in changedValues) updatedParams.new_ata_utc = changedValues.ata;
        if ("etd" in changedValues) updatedParams.new_etd_utc = changedValues.etd;
        if ("eta" in changedValues) updatedParams.new_eta_utc = changedValues.eta;
        if ("atd_icao" in changedValues) updatedParams.new_atd_icao_utc = changedValues.atd_icao;
        if ("ata_icao" in changedValues) updatedParams.new_ata_icao_utc = changedValues.ata_icao;
        if ("delay_code" in changedValues) updatedParams.delay_code = changedValues.delay_code;
        if ("delay_minutes" in changedValues) {
            if (!updatedParams.mvt_full_data) updatedParams.mvt_full_data = {};
            updatedParams.mvt_full_data.delay_duration = changedValues.delay_minutes;
        }
        if ("aircraft_reg" in changedValues) updatedParams.aircraft_reg = changedValues.aircraft_reg;
        onParametersChange(updatedParams);
    };

    const getFieldsToDisplay = () => {
        // const fields = ["flight_number", "departure_airport", "arrival_airport", "std", "sta"]; // 旧的默认值
        const fields = []; // 初始化为空，按需添加

        const fieldMapping = {
            // 映射 action_parameters 中的字段到表单字段
            flight_number: "flight_number",
            departure_airport: "departure_airport",
            arrival_airport: "arrival_airport",
            std: "std",
            sta: "sta",
            new_atd_utc: "atd",
            new_ata_utc: "ata",
            new_atd_icao_utc: "atd_icao",
            new_ata_icao_utc: "ata_icao",
            new_etd_utc: "etd",
            new_eta_utc: "eta",
            delay_code: "delay_code",
            // aircraft_reg 直接用字段名
        };

        // 检查 actualDataParams 中是否有对应建议，有则添加到显示列表
        for (const backendField in fieldMapping) {
            if (actualDataParams.hasOwnProperty(backendField)) {
                const formField = fieldMapping[backendField];
                if (!fields.includes(formField)) {
                    fields.push(formField);
                }
            }
        }

        // 单独处理 mvt_full_data 内的字段
        if (actualDataParams.mvt_full_data?.hasOwnProperty("delay_duration")) {
            if (!fields.includes("delay_minutes")) {
                fields.push("delay_minutes");
            }
        }
        // 单独处理 aircraft_reg (如果它不在 fieldMapping 中但存在于 actualDataParams)
        if (actualDataParams.hasOwnProperty("aircraft_reg")) {
            if (!fields.includes("aircraft_reg")) {
                fields.push("aircraft_reg");
            }
        }

        // 备注字段通常总是需要显示，以便用户可以添加备注
        fields.push("remarks");
        return [...new Set(fields)]; //确保唯一性
    };

    // 渲染航班基本信息卡片
    const renderFlightInfoCard = () => {
        const currentValues = prepareCurrentValues();
        const { flight_number, departure_airport, arrival_airport, std, sta, aircraft_reg } = currentValues;

        // 航班状态标签
        let statusTag = null;
        if (currentFlightData) {
            // 获取状态文本并确保类型安全
            let statusText = String(currentFlightData.status || "UNKNOWN");

            // 根据不同状态设置不同颜色
            let statusColor = "default";
            if (typeof statusText === "string") {
                if (statusText.includes("DEPARTED")) {
                    statusColor = "green";
                } else if (statusText.includes("SCHEDULED")) {
                    statusColor = "blue";
                } else if (statusText.includes("CANCELED")) {
                    statusColor = "red";
                } else if (statusText.includes("COMPLETED")) {
                    statusColor = "purple";
                }
            }

            statusTag = <Tag color={statusColor}>{statusText}</Tag>;
        }

        // 格式化时间间隔
        const formatDuration = (minutes) => {
            if (!minutes || minutes < 0) return "-";
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            const paddedHours = String(hours).padStart(2, "0");
            const paddedMins = String(mins).padStart(2, "0");
            return `${paddedHours}:${paddedMins}`;
        };

        // 计算时间差（分钟）
        const calculateTimeDiff = (time1, time2) => {
            if (!time1 || !time2) return null;
            return dayjs(time1).diff(dayjs(time2), "minute");
        };

        // 根据时间差生成标签颜色
        const getDelayTagColor = (diff) => {
            if (!diff) return "default";
            if (diff <= 0) return "success";
            if (diff <= 15) return "default";
            if (diff <= 30) return "warning";
            return "error";
        };

        // 计算关键时间差异
        const stdToEtdDiff = currentValues.etd ? calculateTimeDiff(currentValues.etd, std) : 0;
        const staToEtaDiff = currentValues.eta ? calculateTimeDiff(currentValues.eta, sta) : 0;
        const stdToAtdDiff = currentValues.atd ? calculateTimeDiff(currentValues.atd, std) : 0;
        const staToAtaDiff = currentValues.ata ? calculateTimeDiff(currentValues.ata, sta) : 0;

        return (
            <Card title="执行航班" size="small" bordered={true} style={{ marginBottom: 16 }}>
                {/* 航班基本信息 */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <Space>
                        <span style={{ fontWeight: "bold" }}>
                            {flight_number} ({departure_airport} → {arrival_airport}, {std ? dayjs.utc(std).format("MM-DD") : "未知"})
                        </span>
                        {aircraft_reg && (
                            <Tag color="cyan" style={{ marginLeft: 4 }}>
                                {aircraft_reg}
                            </Tag>
                        )}
                    </Space>
                    {statusTag}
                </div>

                {/* 航班时间信息 - 表格式布局 */}
                <Row gutter={[8, 8]}>
                    <Col span={5}></Col>
                    <Col span={8} style={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>
                        起飞 ({departure_airport})
                    </Col>
                    <Col span={8} style={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>
                        到达 ({arrival_airport})
                    </Col>
                    <Col span={3} style={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>
                        间隔
                    </Col>
                </Row>
                <Divider style={{ margin: "8px 0" }} />

                {/* 计划时间行 */}
                <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                    <Col span={5} style={{ color: "#1890ff", fontWeight: "500", textAlign: "right" }}>
                        计划
                    </Col>
                    <Col span={8} style={{ textAlign: "right" }}>
                        {std ? <FlightTimeDisplay utcTime={std} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" showDate={true} /> : "-"}
                    </Col>
                    <Col span={8} style={{ textAlign: "right" }}>
                        {sta ? <FlightTimeDisplay utcTime={sta} baseUtcTime={std} airportCode={arrival_airport} airportsMap={airportsMap} format="HH:mm" showDate={true} /> : "-"}
                    </Col>
                    <Col span={3} style={{ textAlign: "right" }}>
                        {std && sta ? formatDuration(dayjs(sta).diff(dayjs(std), "minute")) : "-"}
                    </Col>
                </Row>

                {/* 预计时间行 - 如果有值 */}
                {(currentValues.etd || currentValues.eta) && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                        <Col span={5} style={{ color: "#fa8c16", fontWeight: "500", textAlign: "right" }}>
                            预计
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {currentValues.etd && (
                                <>
                                    {stdToEtdDiff !== 0 && (
                                        <Tag color={getDelayTagColor(stdToEtdDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {stdToEtdDiff > 0 ? `+${stdToEtdDiff}m` : `${stdToEtdDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={currentValues.etd} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" />
                                </>
                            )}
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {currentValues.eta && (
                                <>
                                    {staToEtaDiff !== 0 && (
                                        <Tag color={getDelayTagColor(staToEtaDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {staToEtaDiff > 0 ? `+${staToEtaDiff}m` : `${staToEtaDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={currentValues.eta} baseUtcTime={currentValues.etd || std} airportCode={arrival_airport} airportsMap={airportsMap} format="HH:mm" />
                                </>
                            )}
                        </Col>
                        <Col span={3} style={{ textAlign: "right" }}>
                            {currentValues.etd && currentValues.eta ? formatDuration(dayjs(currentValues.eta).diff(dayjs(currentValues.etd), "minute")) : "-"}
                        </Col>
                    </Row>
                )}

                {/* 实际时间行 - 如果有值 */}
                {(currentValues.atd || currentValues.ata) && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                        <Col span={5} style={{ color: "#52c41a", fontWeight: "500", textAlign: "right" }}>
                            实际
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {currentValues.atd && (
                                <>
                                    {stdToAtdDiff !== 0 && (
                                        <Tag color={getDelayTagColor(stdToAtdDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {stdToAtdDiff > 0 ? `+${stdToAtdDiff}m` : `${stdToAtdDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={currentValues.atd} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" />
                                </>
                            )}
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {currentValues.ata && (
                                <>
                                    {staToAtaDiff !== 0 && (
                                        <Tag color={getDelayTagColor(staToAtaDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {staToAtaDiff > 0 ? `+${staToAtaDiff}m` : `${staToAtaDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={currentValues.ata} baseUtcTime={currentValues.atd || std} airportCode={arrival_airport} airportsMap={airportsMap} format="HH:mm" />
                                </>
                            )}
                        </Col>
                        <Col span={3} style={{ textAlign: "right" }}>
                            {currentValues.atd && currentValues.ata ? formatDuration(dayjs(currentValues.ata).diff(dayjs(currentValues.atd), "minute")) : "-"}
                        </Col>
                    </Row>
                )}

                {/* 离地/着陆时间行 - 如果有值 */}
                {(currentValues.atd_icao || currentValues.ata_icao) && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                        <Col span={5} style={{ color: "#722ed1", fontWeight: "500", textAlign: "right" }}>
                            离地/着陆
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {currentValues.atd_icao && <FlightTimeDisplay utcTime={currentValues.atd_icao} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" />}
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {currentValues.ata_icao && <FlightTimeDisplay utcTime={currentValues.ata_icao} baseUtcTime={currentValues.atd_icao} airportCode={arrival_airport} airportsMap={airportsMap} format="HH:mm" />}
                        </Col>
                        <Col span={3} style={{ textAlign: "right" }}>
                            {currentValues.atd_icao && currentValues.ata_icao ? formatDuration(dayjs(currentValues.ata_icao).diff(dayjs(currentValues.atd_icao), "minute")) : "-"}
                        </Col>
                    </Row>
                )}

                {/* 延误信息 - 如果有值 */}
                {currentValues.delay_code && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px", marginTop: 8 }}>
                        <Col span={24}>
                            <div style={{ padding: "6px 8px", background: "#fffbe6", borderRadius: "4px" }}>
                                <b>延误代码:</b> {currentValues.delay_code}
                                {currentValues.delay_minutes && <> ({currentValues.delay_minutes}分钟)</>}
                            </div>
                        </Col>
                    </Row>
                )}
            </Card>
        );
    };

    // 渲染原始MVT报文
    const renderMvtRawContent = () => {
        if (!rawContent) return null;

        return (
            <Card size="small" title="原始 MVT 报文" style={{ marginBottom: 16 }}>
                <div
                    style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        whiteSpace: "pre-wrap",
                        background: "#f5f5f5",
                        padding: "12px",
                        borderRadius: "4px",
                        maxHeight: "150px",
                        overflowY: "auto",
                    }}
                >
                    {rawContent}
                </div>
            </Card>
        );
    };

    const suggestedValuesForDisplay = prepareSuggestedValues();
    const noNewSuggestions = Object.keys(suggestedValuesForDisplay).length === 0;

    if (!currentFlightData && !Object.keys(contextFlightDetails).length) {
        // 如果 currentFlightData 和 contextFlightDetails 都为空，则显示特定提示
        return (
            <div>
                <Alert message="航班基础数据缺失" description="无法获取当前航班的基本信息，部分数据显示可能不完整。" type="warning" showIcon style={{ marginBottom: 16 }} />
                {renderMvtRawContent()}
                <FlightInfoForm
                    currentValues={prepareCurrentValues()}
                    suggestedValues={suggestedValuesForDisplay}
                    onValuesChange={handleValuesChange}
                    fieldsToDisplay={getFieldsToDisplay()}
                    fieldConfig={{
                        displayMode: "compare",
                        highlightChanged: true,
                    }}
                />
            </div>
        );
    }

    return (
        <div>
            {/* 展示匹配航班信息卡片 */}
            {renderFlightInfoCard()}

            {/* 展示原始MVT报文 */}
            {renderMvtRawContent()}

            {/* 修改建议表单 */}
            <Title level={5} style={{ marginBottom: 12 }}>
                MVT报文建议更新
            </Title>
            {noNewSuggestions && <Alert message="无新的更新建议" description="此MVT报文未提供新的、不同于当前航班状态的更新建议，或建议的字段当前不可编辑。" type="info" showIcon style={{ marginBottom: 16 }} />}
            <FlightInfoForm
                currentValues={prepareCurrentValues()}
                suggestedValues={suggestedValuesForDisplay}
                onValuesChange={handleValuesChange}
                fieldsToDisplay={getFieldsToDisplay()}
                fieldConfig={{
                    displayMode: "compare",
                    highlightChanged: true,
                }}
            />
        </div>
    );
};

export default MvtFlightUpdateHandler;
